import React, { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import ProjectCard from './components/ProjectCard';
import FilterPanel from './components/FilterPanel';
import CompanyCredentials from './components/CompanyCredentials';
import ProjectStats from './components/ProjectStats';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';


const ProjectPortfolio = () => {
  const [showFilters, setShowFilters] = useState(false);

  const [filters, setFilters] = useState({
    status: 'all',
    priceRange: 'all',
    location: 'all',
    type: 'all',
    search: ''
  });

  // Only Residencial 3 Volcanes project
  const projects = [
  {
    id: 'residencial-3-volcanes',
    name: 'Residencial 3 Volcanes',
    location: 'Cayma, Arequipa',
    image: "https://images.unsplash.com/photo-1647777409425-c49f5036e8cb",
    imageAlt: 'Modern residential development with contemporary architecture featuring white facades and large windows set against volcanic mountain backdrop in Arequipa Peru',
    status: 'En Venta',
    totalLots: 120,
    availableLots: 87,
    priceRange: 'S/ 180,000 - S/ 320,000',
    completionDate: 'Diciembre 2026',
    type: 'Residencial',
    features: ['Áreas Verdes', 'Seguridad 24/7', 'Club House', 'Parque Infantil']
  }];


  const stats = {
    activeProjects: projects?.filter((p) => p?.status === 'En Venta')?.length,
    totalLots: projects?.reduce((sum, p) => sum + p?.totalLots, 0),
    availableLots: projects?.reduce((sum, p) => sum + p?.availableLots, 0),
    completedProjects: projects?.filter((p) => p?.status === 'Agotado')?.length
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleClearFilters = () => {
    setFilters({
      status: 'all',
      priceRange: 'all',
      location: 'all',
      type: 'all',
      search: ''
    });
  };

  const filteredProjects = useMemo(() => {
    return projects?.filter((project) => {
      if (filters?.status !== 'all' && project?.status !== filters?.status) {
        return false;
      }

      if (filters?.location !== 'all' && !project?.location?.includes(filters?.location)) {
        return false;
      }

      if (filters?.type !== 'all' && project?.type !== filters?.type) {
        return false;
      }

      if (filters?.priceRange !== 'all') {
        const priceValue = parseInt(project?.priceRange?.match(/\d+/)?.[0]);
        const [min, max] = filters?.priceRange?.split('-')?.map((v) => parseInt(v?.replace('+', '')));

        if (filters?.priceRange?.includes('+')) {
          if (priceValue < min) return false;
        } else {
          if (priceValue < min || priceValue > max) return false;
        }
      }

      if (filters?.search) {
        const searchLower = filters?.search?.toLowerCase();
        return project?.name?.toLowerCase()?.includes(searchLower) ||
        project?.location?.toLowerCase()?.includes(searchLower) || project?.features?.some((f) => f?.toLowerCase()?.includes(searchLower));
      }

      return true;
    });
  }, [filters, projects]);

  const featuredProject = projects?.find((p) => p?.id === 'residencial-3-volcanes');
  const otherProjects = filteredProjects?.filter((p) => p?.id !== 'residencial-3-volcanes');

  const breadcrumbItems = [
    { label: 'Inicio', path: '/' },
    { label: 'Proyectos', path: '/proyectos' }
  ];

  return (
    <>
      <Helmet>
        <title>Portafolio de Proyectos - Residencial 3 Volcanes | Inversiones Inmobiliarias</title>
        <meta
          name="description"
          content="Explora nuestro portafolio de proyectos inmobiliarios en Arequipa. Lotes residenciales y comerciales con ubicaciones estratégicas y financiamiento flexible."
        />
        <meta property="og:title" content="Portafolio de Proyectos - Residencial 3 Volcanes" />
        <meta property="og:description" content="Descubre nuestros proyectos inmobiliarios en Arequipa" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <NavigationBar />

        <div className="container mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6 lg:py-8 mt-16 sm:mt-20">
          <BreadcrumbTrail items={breadcrumbItems} />

          {/* Header - Mobile optimized */}
          <div className="mb-4 sm:mb-6 lg:mb-8">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div>
                <h1 className="text-xl sm:text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                  Portafolio de Proyectos
                </h1>
                <p className="text-xs sm:text-sm md:text-base text-muted-foreground">
                  Explora nuestros proyectos inmobiliarios en Arequipa
                </p>
              </div>

              {/* Action Buttons - Mobile optimized */}
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <Button
                  variant="outline"
                  iconName="Filter"
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-xs sm:text-sm"
                >
                  {showFilters ? 'Ocultar Filtros' : 'Mostrar Filtros'}
                </Button>
                <Button
                  variant="outline"
                  iconName="Download"
                  onClick={() => console.log('Export projects')}
                  className="text-xs sm:text-sm"
                >
                  Exportar
                </Button>
              </div>
            </div>
          </div>

          {/* Project Stats - Mobile optimized grid */}
          <div className="mb-4 sm:mb-6">
            <ProjectStats stats={stats} />
          </div>

          {/* Main Content Grid - Mobile stacked */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-6">
            {/* Filters Panel - Collapsible on mobile */}
            {showFilters && (
              <div className="lg:col-span-3">
                <div className="card p-4 sm:p-5 sticky top-24">
                  <FilterPanel
                    filters={filters}
                    onFilterChange={handleFilterChange}
                    onClearFilters={handleClearFilters}
                  />
                </div>
              </div>
            )}

            {/* Projects Grid - Full width on mobile */}
            <div className={showFilters ? 'lg:col-span-9' : 'lg:col-span-12'}>
              {filteredProjects?.length === 0 ? (
                <div className="card p-8 sm:p-12 text-center">
                  <Icon name="Search" size={48} className="mx-auto mb-4 text-muted-foreground" />
                  <h3 className="text-lg sm:text-xl font-semibold text-foreground mb-2">
                    No se encontraron proyectos
                  </h3>
                  <p className="text-sm sm:text-base text-muted-foreground mb-4">
                    Intenta ajustar los filtros para ver más resultados
                  </p>
                  <Button variant="outline" onClick={handleClearFilters} className="text-xs sm:text-sm">
                    Limpiar Filtros
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 sm:gap-6">
                  {filteredProjects?.map((project) => (
                    <ProjectCard key={project?.id} project={project} />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Company Credentials - Mobile optimized */}
          <div className="mt-8 sm:mt-12 lg:mt-16">
            <CompanyCredentials />
          </div>
        </div>
      </div>
    </>
  );
};

export default ProjectPortfolio;